// src/pages/PlayableGames.js
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PlayableGameCard from "../components/PlayableGameCard";
import { useAllGames } from "../hooks/useAllGames";

export default function PlayableGames() {
  const navigate = useNavigate();
  const { games, loading } = useAllGames();

  const [query, setQuery] = useState("");
  const [genre, setGenre] = useState("All");
  const [tag, setTag] = useState("All");

  // Only games that have a webglPath are playable on-site
  const playableGames = useMemo(
    () =>
      games.filter(
        (g) => g.webglPath && String(g.webglPath).trim() !== ""
      ),
    [games]
  );

  const genres = useMemo(() => {
    const set = new Set();
    playableGames.forEach((g) => g.genre && set.add(g.genre));
    return ["All", ...Array.from(set).sort()];
  }, [playableGames]);

  const tags = useMemo(() => {
    const set = new Set();
    playableGames.forEach((g) => (g.tags || []).forEach((t) => set.add(t)));
    return ["All", ...Array.from(set).sort()];
  }, [playableGames]);

  const results = useMemo(() => {
    let list = [...playableGames];

    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((g) => {
        const haystack = [
          g.title,
          g.description,
          g.genre,
          ...(g.tags || []),
          g.source,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    if (genre !== "All") {
      list = list.filter((g) => g.genre === genre);
    }

    if (tag !== "All") {
      list = list.filter((g) => (g.tags || []).includes(tag));
    }

    // Kepithor first, then others
    list.sort((a, b) => {
      const aIsK = (a.source || "").toLowerCase() === "kepithor";
      const bIsK = (b.source || "").toLowerCase() === "kepithor";
      if (aIsK && !bIsK) return -1;
      if (bIsK && !aIsK) return 1;
      return a.title.localeCompare(b.title);
    });

    return list;
  }, [playableGames, query, genre, tag]);

  return (
    <div className="grid gap-6">
      <header>
        <h1 className="text-3xl font-bold tracking-tight">
          Playable on Kepithor
        </h1>
        <p className="text-sm text-slate-300 mt-1">
          Games you can play instantly in your browser, including Kepithor
          titles and partner games.
        </p>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center bg-slate-900/60 border border-slate-800 rounded-2xl px-4 py-3 shadow-sm shadow-emerald-500/10">
        <input
          placeholder="Search by title, description, or source…"
          className="border border-slate-700 bg-slate-900/80 rounded-xl px-3 py-2 min-w-[240px] text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        <select
          className="border border-slate-700 bg-slate-900/80 rounded-xl px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          value={genre}
          onChange={(e) => setGenre(e.target.value)}
        >
          {genres.map((g) => (
            <option key={g} value={g}>
              {g === "All" ? "All genres" : g}
            </option>
          ))}
        </select>

        <select
          className="border border-slate-700 bg-slate-900/80 rounded-xl px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
        >
          {tags.map((t) => (
            <option key={t} value={t}>
              {t === "All" ? "All tags" : t}
            </option>
          ))}
        </select>

      </div>

      {/* Results */}
      {loading && results.length === 0 ? (
        <div className="text-sm text-slate-400">Loading games…</div>
      ) : results.length === 0 ? (
        <div className="text-sm text-slate-400">
          No playable games match your filters.
        </div>
      ) : (
        <div className="space-y-3">
          {results.map((game) => (
            <PlayableGameCard
              key={game.id}
              game={game}
              onOpen={() => navigate(`/game/${game.id}`)}
              onPlay={() => navigate(`/play/${game.id}`)}
            />
          ))}
        </div>
      )}
      
        <div className="spacer" style={{height: 100}}></div>
    </div>
  );
}
